"use strict";
/**
 * APITable <https://github.com/apitable/apitable>
 * Copyright (C) 2022 APITable Ltd. <https://apitable.com>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Database = void 0;
const command_manager_1 = require("../../command_manager");
const datasheet_1 = require("./datasheet");
const dashboard_1 = require("./dashboard");
/**
 * A database is responsible for providing `Datasheet` instances.
 *
 * Conceptually, one database corresponds to one space in APITable.
 */
class Database {
    constructor() {
        this.commandManagers = new WeakMap();
        this._eventHandlers = new Map();
    }
    /**
     * Set the data storage provider for the database.
     *
     * **NOTE**: A data storage provider must be set before loading datasheets.
     */
    setDataStorageProvider(provider) {
        this.storageProvider = provider;
    }
    /**
     * Set the store provider that is responsible for providing internal redux stores for datasheets in this database.
     *
     * **NOTE**: A store provider must be set before loading datasheets.
     */
    setStoreProvider(provider) {
        this.storeProvider = provider;
    }
    /**
     * Load a datasheet in the database.
     *
     * **NOTE**: A data storage provider and a store provider must be set before loading datasheets.
     */
    getDatasheet(dstId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const { loadOptions } = options;
            const datasheetPack = yield this.storageProvider.loadDatasheetPack(dstId, loadOptions);
            if (datasheetPack === null) {
                return null;
            }
            const store = 'createStore' in options
                ? yield options.createStore(datasheetPack)
                : yield this.storeProvider.createDatasheetStore(datasheetPack, options.storeOptions);
            const datasheet = new datasheet_1.Datasheet(dstId, {
                store,
                saver: this.storageProvider,
                commandManager: this.getCommandManager(store),
            });
            return datasheet;
        });
    }
    getDashboard(dsbId, options) {
        return __awaiter(this, void 0, void 0, function* () {
            const { loadOptions } = options;
            const dashboardPack = yield this.storageProvider.loadDashboardPack(dsbId, loadOptions);
            if (dashboardPack === null) {
                return null;
            }
            const store = 'createStore' in options
                ? yield options.createStore(dashboardPack)
                : yield this.storeProvider.createDashboardStore(dashboardPack, options.storeOptions);
            const dashboard = new dashboard_1.Dashboard(dsbId, {
                store,
                saver: this.storageProvider,
                widgetMap: dashboardPack.widgetMap,
                commandManager: this.getCommandManager(store),
            });
            return dashboard;
        });
    }
    getCommandManager(store) {
        if (this.commandManagers.has(store)) {
            return this.commandManagers.get(store);
        }
        const commandManager = new command_manager_1.CollaCommandManager({
            handleCommandExecuted: (resourceOpCollections) => {
                // FIXME should await
                void this.fireEvent({
                    type: 1 /* ResourceEventType.CommandExecuted */,
                    execResult: 0 /* CommandExecutionResultType.Success */,
                    resourceOpCollections,
                });
            },
            handleCommandExecuteError: (error, errorType) => {
                // FIXME should await
                void this.fireEvent({
                    type: 1 /* ResourceEventType.CommandExecuted */,
                    execResult: 1 /* CommandExecutionResultType.Error */,
                    error,
                    errorType,
                });
            },
        }, store);
        this.commandManagers.set(store, commandManager);
        return commandManager;
    }
    /**
     * Add an event handler to the database.
     *
     * @returns `true` if the event handler was successfully added. `false` if the same handler was previously added.
     */
    addEventHandler(handler) {
        let handlers = this._eventHandlers.get(handler.type);
        if (handlers === undefined) {
            handlers = new Set();
            this._eventHandlers.set(handler.type, handlers);
        }
        if (handlers.has(handler)) {
            return false;
        }
        handlers.add(handler);
        return true;
    }
    /**
     * Remove an event handler from the database.
     *
     * @returns `true` if the event handler was successfully removed. `false` if the handler did not exist in the datasheet.
     */
    removeEventHandler(handler) {
        let handlers = this._eventHandlers.get(handler.type);
        if (handlers === undefined) {
            handlers = new Set();
            this._eventHandlers.set(handler.type, handlers);
        }
        return handlers.delete(handler);
    }
    /**
     * Remove all event handles of a specific type from the database.
     */
    removeEventHandlers(type) {
        this._eventHandlers.delete(type);
    }
    /**
     * Fire an event in the database, invoking corresponding event handlers.
     */
    fireEvent(event) {
        return __awaiter(this, void 0, void 0, function* () {
            const handlers = this._eventHandlers.get(event.type);
            if (handlers) {
                for (const handler of handlers) {
                    yield handler.handle(event);
                }
            }
        });
    }
}
exports.Database = Database;
//# sourceMappingURL=database.js.map